import React, { useState, useEffect } from 'react';
import dhyanaService from '../services/dhyanaService';

interface TestResult {
  name: string;
  status: 'pending' | 'loading' | 'success' | 'error';
  data?: any;
  error?: string;
  duration?: number;
}

const DebugPage: React.FC = () => {
  const [testResults, setTestResults] = useState<TestResult[]>([
    { name: 'Load Config (Personas & Stages)', status: 'pending' },
    { name: 'Generate Landing Content', status: 'pending' },
    { name: 'Classify Persona', status: 'pending' },
    { name: 'Submit Newsletter Signup', status: 'pending' },
    { name: 'Cache Stats', status: 'pending' },
  ]);

  const [stylingTest, setStylingTest] = useState({
    utilityClasses: true,
    animations: false,
    colors: true,
  });

  const updateTestResult = (index: number, updates: Partial<TestResult>) => {
    setTestResults(prev => 
      prev.map((result, i) => i === index ? { ...result, ...updates } : result)
    );
  };

  const runTest = async (testIndex: number, testFn: () => Promise<any>, testName: string) => {
    const startTime = Date.now();
    updateTestResult(testIndex, { status: 'loading' });
    
    try {
      const result = await testFn();
      const duration = Date.now() - startTime;
      updateTestResult(testIndex, { 
        status: 'success', 
        data: result, 
        duration 
      });
    } catch (error) {
      const duration = Date.now() - startTime;
      updateTestResult(testIndex, { 
        status: 'error', 
        error: error instanceof Error ? error.message : String(error),
        duration 
      });
    }
  };

  const runAllTests = async () => {
    // Test 1: Load Config
    await runTest(0, async () => {
      const config = await dhyanaService.loadConfig();
      return {
        personas: Object.keys(config.personas || {}).length,
        stages: Object.keys(config.stages || {}).length,
        loaded: config.loaded
      };
    }, 'Load Config');

    // Test 2: Generate Content
    await runTest(1, async () => {
      return await dhyanaService.generateLandingContent(1, 'high_technical');
    }, 'Generate Content');

    // Test 3: Classify Persona
    await runTest(2, async () => {
      return await dhyanaService.classifyPersona({
        email: 'test@example.com',
        interests: 'AI, automation, technical content',
        role: 'developer'
      });
    }, 'Classify Persona');

    // Test 4: Newsletter Signup
    await runTest(3, async () => {
      return await dhyanaService.submitSignup('test@example.com', 'high_technical');
    }, 'Newsletter Signup');

    // Test 5: Cache Stats
    await runTest(4, async () => {
      return dhyanaService.getCacheStats();
    }, 'Cache Stats');
  };

  const testAnimations = () => {
    setStylingTest(prev => ({ ...prev, animations: !prev.animations }));
    setTimeout(() => {
      setStylingTest(prev => ({ ...prev, animations: false }));
    }, 3000);
  };

  useEffect(() => {
    // Auto-run tests on mount
    runAllTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'loading': return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 inline-block"></div>;
      case 'success': return '✅';
      case 'error': return '❌';
    }
  };

  const getStatusColor = (status: TestResult['status']) => {
    switch (status) {
      case 'pending': return 'text-gray-500';
      case 'loading': return 'text-blue-600';
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-blue-600 mb-2">Dhyana Frontend Debug</h1>
          <p className="text-gray-600">API Integration & Styling Validation</p>
        </div>

        {/* Styling Tests */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">🎨 Styling Tests</h2>
          
          {/* Utility Classes Demo */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Utility Classes</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center justify-center h-20 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-blue-600 font-medium">Flexbox Center</span>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-green-600 font-bold">Text Center</div>
                <div className="text-sm text-gray-500">With padding</div>
              </div>
              <div className="bg-gradient-to-r from-purple-400 to-pink-400 text-white p-4 rounded-lg">
                <span className="font-semibold">Custom Gradient</span>
              </div>
            </div>
          </div>

          {/* Animation Test */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Animations</h3>
            <div className="flex items-center gap-4">
              <button
                onClick={testAnimations}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Test Spinner
              </button>
              {stylingTest.animations && (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              )}
              <span className="text-gray-600">
                {stylingTest.animations ? 'Animating...' : 'Click to test'}
              </span>
            </div>
          </div>

          {/* Color Palette */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Color Palette</h3>
            <div className="flex gap-2 flex-wrap">
              {[
                { name: 'Blue', class: 'bg-blue-600' },
                { name: 'Green', class: 'bg-green-600' },
                { name: 'Red', class: 'bg-red-600' },
                { name: 'Purple', class: 'bg-purple-600' },
                { name: 'Gray', class: 'bg-gray-600' },
              ].map(color => (
                <div key={color.name} className="text-center">
                  <div className={`w-12 h-12 rounded-lg ${color.class} mb-1`}></div>
                  <span className="text-xs text-gray-500">{color.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* API Tests */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">🚀 API Tests</h2>
            <button
              onClick={runAllTests}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Re-run All Tests
            </button>
          </div>

          <div className="space-y-4">
            {testResults.map((result, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(result.status)}
                    <span className="font-medium text-gray-800">{result.name}</span>
                    {result.duration && (
                      <span className="text-sm text-gray-500">({result.duration}ms)</span>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${getStatusColor(result.status)}`}>
                    {result.status.toUpperCase()}
                  </span>
                </div>

                {result.error && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 mb-2">
                    <p className="text-red-700 text-sm font-medium">Error:</p>
                    <p className="text-red-600 text-sm">{result.error}</p>
                  </div>
                )}

                {result.data && (
                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <p className="text-gray-700 text-sm font-medium mb-1">Response:</p>
                    <pre className="text-xs text-gray-600 overflow-x-auto">
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* System Info */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">🔧 System Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong>API Base:</strong> {process.env.REACT_APP_DHYANA_BRAIN_URL || 'Default'}
            </div>
            <div>
              <strong>Debug Mode:</strong> {process.env.REACT_APP_DEBUG_MODE || 'false'}
            </div>
            <div>
              <strong>Environment:</strong> {process.env.NODE_ENV}
            </div>
            <div>
              <strong>Timestamp:</strong> {new Date().toISOString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebugPage;